import IBAN from 'iban'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect } from 'react'
import { Col, FormGroup, Row } from 'react-bootstrap'

import { useForm, UseFormReturn } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import BreadCrumbs from 'src/Components/Authenticated/BreadCrumbs'
import Portlet from 'src/Components/Authenticated/Portlet'
import LoaderIcon from 'src/Components/LoaderIcon'
import McButton from 'src/Components/McButton'
import { MainPage, MyDropzone, MySelect, MyTextbox } from 'src/Components/My'
import { ReadFileContent } from 'src/Helpers/FileHelper'
import { IBankSelectOption } from 'src/Models/Server/Response/Bank/IBankDTO'
import { t } from '../../../../../I18n'
import { automaticChargeInsertStore, IFormValues } from './Store'

const Insert: React.FC = () => {
  const params = useParams<'id' | 'email'>()
  const { id, email } = params

  const { User, Currencies, Banks, IsLoading, ViewModel, IsSubmitting } =
    automaticChargeInsertStore

  useEffect(() => {
    automaticChargeInsertStore.Initialize(id, email)
  }, [null])

  useEffect(() => {
    reset(ViewModel)
  }, [ViewModel])

  const useFormProps: UseFormReturn<IFormValues> = useForm<IFormValues>({})
  const { handleSubmit, reset, getValues, setValue } = useFormProps

  const onSubmit = (data: IFormValues) => {
    automaticChargeInsertStore.Insert(data)
  }

  return (
    <MainPage>
      <LoaderIcon IsLoading={IsLoading} />

      <br className="xs-10" />
      {!IsLoading && User !== null && (
        <Fragment>
          <BreadCrumbs
            currentPageTitle={t.automaticChargeInsert.title}
            previousPages={[
              {
                to: '/helpdesk/users',
                title: t.helpdesk.title,
              },
              {
                to: `/user/details/${id}`,
                title: User.Name,
              },
            ]}
          />
          <form className="parsley-form" onSubmit={handleSubmit(onSubmit)}>
            <Portlet title={t.common.details}>
              <Row>
                <Col md={6}>
                  <MySelect
                    formObject={{} as IFormValues}
                    useFormProps={useFormProps}
                    label={t.common.currency}
                    name="SelectedCurrency"
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.SelectedCurrency.errors
                          .required,
                    }}
                    infoText={
                      t.automaticChargeInsert.fields.SelectedCurrency.info
                    }
                    options={Currencies}
                  />
                  <Row>
                    <Col sm={6}>
                      <MyTextbox
                        formObject={{} as IFormValues}
                        label={t.automaticChargeInsert.fields.StepAmount.label}
                        infoText={
                          t.automaticChargeInsert.fields.StepAmount.info
                        }
                        name="StepAmount"
                        useFormProps={useFormProps}
                        rules={{
                          required:
                            t.automaticChargeInsert.fields.StepAmount.errors
                              .required,
                        }}
                        type="number"
                        min={10}
                        max={150}
                      />
                    </Col>
                    <Col sm={6}>
                      <MyTextbox
                        formObject={{} as IFormValues}
                        label={t.automaticChargeInsert.fields.StepLimit.label}
                        infoText={t.automaticChargeInsert.fields.StepLimit.info}
                        name="StepLimit"
                        useFormProps={useFormProps}
                        rules={{
                          required:
                            t.automaticChargeInsert.fields.StepLimit.errors
                              .required,
                        }}
                        type="number"
                        min={1}
                        max={10}
                      />
                    </Col>
                  </Row>
                  <MySelect
                    formObject={{} as IFormValues}
                    useFormProps={useFormProps}
                    name="SelectedBank"
                    label={t.automaticChargeInsert.fields.SelectedBank.label}
                    infoText={t.automaticChargeInsert.fields.SelectedBank.info}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.SelectedBank.errors
                          .required,
                      validate: (bank: IBankSelectOption) => {
                        const accountNr = getValues('AccountNumber')
                        //check when both are set
                        if (bank.BankId !== null && accountNr !== '') {
                          return (
                            accountNr.indexOf(bank.BankId) > -1 ||
                            t.automaticChargeInsert.fields.SelectedBank.errors
                              .bank
                          )
                        }
                        return true
                      },
                    }}
                    options={Banks}
                  />
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.AccountNumber.label}
                    name="AccountNumber"
                    useFormProps={useFormProps}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.AccountNumber.errors
                          .required,
                      validate: (value) => {
                        return (
                          IBAN.isValid(value) ||
                          t.automaticChargeInsert.fields.AccountNumber.errors
                            .iban
                        )
                      },
                    }}
                    maxLength={50}
                  />
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.AccountHolder.label}
                    name="AccountHolder"
                    useFormProps={useFormProps}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.AccountNumber.errors
                          .required,
                    }}
                    maxLength={70}
                  />

                  <FormGroup>
                    <MyDropzone
                      formObject={{} as IFormValues}
                      useFormProps={useFormProps}
                      name="SepaFile"
                      label={t.automaticChargeInsert.fields.SepaFileData.label}
                      infoText={
                        t.automaticChargeInsert.fields.SepaFileData.info
                      }
                      dropFileText={
                        t.automaticChargeInsert.fields.SepaFileData.dropFile
                      }
                      rules={{
                        required:
                          t.automaticChargeInsert.fields.SepaFileData.errors
                            .required,
                      }}
                      accept={['image/jpeg', 'image/png', 'application/pdf']}
                      onChangeFile={(files: File[] | null) => {
                        if (files === null) {
                          //removed
                          setValue('SepaFileData', '')
                          setValue('MimeType', '')
                        } else {
                          ReadFileContent(files, (base64, file) => {
                            setValue('SepaFileData', base64)
                            setValue('MimeType', file.type)
                          })
                        }
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.LastName.label}
                    name="LastName"
                    useFormProps={useFormProps}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.LastName.errors.required,
                    }}
                    maxLength={50}
                  />
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.EmailAddress.label}
                    name="EmailAddress"
                    useFormProps={useFormProps}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.EmailAddress.errors
                          .required,
                    }}
                    maxLength={100}
                  />
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.StreetName.label}
                    name="StreetName"
                    useFormProps={useFormProps}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.StreetName.errors
                          .required,
                    }}
                    maxLength={100}
                  />
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.StreetNumber.label}
                    name="StreetNumber"
                    useFormProps={useFormProps}
                    rules={{
                      required:
                        t.automaticChargeInsert.fields.StreetNumber.errors
                          .required,
                    }}
                    maxLength={10}
                  />
                  <Row>
                    <Col md={4}>
                      <MyTextbox
                        formObject={{} as IFormValues}
                        label={t.automaticChargeInsert.fields.Zipcode.label}
                        name="Zipcode"
                        useFormProps={useFormProps}
                        rules={{
                          required:
                            t.automaticChargeInsert.fields.Zipcode.errors
                              .required,
                        }}
                        maxLength={10}
                      />
                    </Col>
                    <Col md={8}>
                      <MyTextbox
                        formObject={{} as IFormValues}
                        label={t.automaticChargeInsert.fields.City.label}
                        name="City"
                        useFormProps={useFormProps}
                        rules={{
                          required:
                            t.automaticChargeInsert.fields.City.errors.required,
                        }}
                        maxLength={50}
                      />
                    </Col>
                  </Row>
                  <MyTextbox
                    formObject={{} as IFormValues}
                    label={t.automaticChargeInsert.fields.PhoneNumber.label}
                    name="PhoneNumber"
                    useFormProps={useFormProps}
                    maxLength={20}
                  />
                </Col>
              </Row>
              <hr />
              <Row>
                <Col mdOffset={8} md={4}>
                  <McButton
                    block
                    bsStyle="primary"
                    type="submit"
                    isLoading={IsSubmitting}
                  >
                    {t.common.save}
                  </McButton>
                </Col>
              </Row>
            </Portlet>
          </form>
        </Fragment>
      )}
    </MainPage>
  )
}

export default observer(Insert)
